import type {
  VesselEvaluationDashboard,
  VesselEvaluationDashboardRequest,
  VesselEvaluationFilterKey,
  VesselEvaluationVessel,
} from "../models/vessel-evaluation.models";
import {
  filterVesselEvaluations,
  getVesselEvaluationFilterOptions,
  groupVesselEvaluationsByVesselName,
  sortVesselEvaluations,
} from "../utils/vessel-evaluation-selectors.utils";

type VesselEvaluationMockCoreKey =
  | "buildDate"
  | "contractType"
  | "deliveryDate"
  | "design"
  | "dwt"
  | "engineType"
  | "eplMcrLimit"
  | "imo"
  | "maxRedeliveryDate"
  | "mcr"
  | "minRedeliveryDate"
  | "paintVendor"
  | "scrubber"
  | "status"
  | "vesselName"
  | "vesselType"
  | "yard";

type VesselEvaluationMockCore = Pick<VesselEvaluationVessel, VesselEvaluationMockCoreKey> &
  Partial<Omit<VesselEvaluationVessel, VesselEvaluationMockCoreKey>>;

const EMPTY_VESSEL_PERFORMANCE_FIELDS: Omit<
  VesselEvaluationVessel,
  VesselEvaluationMockCoreKey
> = {
  esd: null,
  ladenDraftCorrection: null,
  ballastDraftCorrection: null,
  refAe: null,
  cleanHullSpeedBallastEco: null,
  consBallastEco: null,
  currentSpeedBallastEco: null,
  cleanHullSpeedBallastFull: null,
  consBallastFull: null,
  currentSpeedBallastFull: null,
  cleanHullSpeedLadenEco: null,
  consLadenEco: null,
  currentSpeedLadenEco: null,
  cleanHullSpeedLadenFull: null,
  consLadenFull: null,
  currentSpeedLadenFull: null,
  comment: null,
  currentAt115Knt: null,
  cpAt115Knt: null,
  cleanAt115Knt: null,
  currentAt125Knt: null,
  cpAt125Knt: null,
  cleanAt125Knt: null,
  evaluatedVps: null,
  cpBallastEcoSpeedCons: null,
  cpBallastFullSpeedCons: null,
  cpLadenEcoSpeedCons: null,
  cpLadenFullSpeedCons: null,
  lastDryDock: null,
  antifoulingAppliedDate: null,
  fb: null,
  vs: null,
  bt: null,
  propellerCoating: null,
  paintSchemeFor: null,
  dryDockComment: null,
  latestHullEvent: null,
  hullCleaned: null,
  propellerPolished: null,
  placeVendor: null,
  minMcrPercent: null,
  maxMcrPercent: null,
};

const createMockVessel = (vessel: VesselEvaluationMockCore): VesselEvaluationVessel => ({
  ...EMPTY_VESSEL_PERFORMANCE_FIELDS,
  ...vessel,
});

// Keeps the existing review sample recognizable while populating fields beyond the initial screenshot viewport.
const MOCK_VESSELS: VesselEvaluationVessel[] = [
  createMockVessel({ vesselName: "AC KATHRYN", design: "Kanda 32BC", vesselType: "HANDY", imo: "9310745", status: "Redelivered", deliveryDate: "2025-11-02", minRedeliveryDate: "2025-12-20", maxRedeliveryDate: "2026-01-09", contractType: "Period TC", dwt: 32728, buildDate: "2006-08-01", yard: "Kanda Kawajiri", engineType: "6UEC52LA", mcr: 6620, eplMcrLimit: null, scrubber: false, esd: "No", paintVendor: "Hempel", cleanHullSpeedBallastEco: 11.5, consBallastEco: 15.8, currentSpeedBallastEco: 11.2, cleanHullSpeedBallastFull: 13.2, consBallastFull: 22.4, currentSpeedBallastFull: 12.8, evaluatedVps: "2025-10-28", lastDryDock: "2023-08-14", antifoulingAppliedDate: "2023-08-12", propellerCoating: "Yes", paintSchemeFor: "60 months", latestHullEvent: "Hull inspection", minMcrPercent: 35, maxMcrPercent: 85 }),
  createMockVessel({ vesselName: "ADALINE", design: "High Bulk 34E", vesselType: "HANDY", imo: "9701243", status: "Redelivered", deliveryDate: "2024-08-12", minRedeliveryDate: "2027-07-01", maxRedeliveryDate: "2027-09-30", contractType: "Period TC", dwt: 34334, buildDate: "2015-09-01", yard: "Namura Shipbuilding - Imari", engineType: "6S46ME", mcr: 5720, eplMcrLimit: 4912, scrubber: false, esd: "No", paintVendor: "Jotun", cleanHullSpeedLadenEco: 11.8, consLadenEco: 18.1, currentSpeedLadenEco: 11.6, evaluatedVps: "2025-08-12", lastDryDock: "2025-02-20", propellerCoating: "No", paintSchemeFor: "90 months" }),
  createMockVessel({ vesselName: "ADASTAR", design: "High Bulk 34E", vesselType: "HANDY", imo: "9701231", status: "Delivered", deliveryDate: "2024-07-17", minRedeliveryDate: "2024-08-16", maxRedeliveryDate: "2024-08-21", contractType: "Trip TC", dwt: 34334, buildDate: "2015-09-01", yard: "Namura Shipbuilding - Imari", engineType: "6S46ME", mcr: 5720, eplMcrLimit: 5045, scrubber: false, esd: "No", paintVendor: "International", comment: "Recent evaluation available", currentAt115Knt: 18.5, cpAt115Knt: 19.2, cleanAt115Knt: 17.8, evaluatedVps: "2024-07-19" }),
  createMockVessel({ vesselName: "ADASUN", design: "High Bulk 34E", vesselType: "HANDY", imo: "9747053", status: "Delivered", deliveryDate: "2024-12-07", minRedeliveryDate: "2027-01-01", maxRedeliveryDate: "2027-03-31", contractType: "Period TC", dwt: 34483, buildDate: "2017-06-01", yard: "Hakodate Dock - Hakodate", engineType: "6S46ME", mcr: 0, eplMcrLimit: 0, scrubber: false, esd: "No", paintVendor: "Hempel" }),
  createMockVessel({ vesselName: "ADELAIDE", design: "SDARI KMAX82NEW", vesselType: "PANAMAX", imo: "9957622", status: "Delivered", deliveryDate: "2025-12-25", minRedeliveryDate: "2026-09-10", maxRedeliveryDate: "2027-01-09", contractType: "Period TC", dwt: 82388, buildDate: "2023-07-01", yard: "COSCO Shipping HI Yangzhou", engineType: "6S60ME-C10", mcr: null, eplMcrLimit: null, scrubber: false, esd: "Yes", paintVendor: "Jotun" }),
  createMockVessel({ vesselName: "AEOLIA", design: "Crown 63", vesselType: "SUPRAMAX", imo: "9736157", status: "Redelivered", deliveryDate: "2026-04-09", minRedeliveryDate: "2026-06-08", maxRedeliveryDate: "2026-06-08", contractType: "Trip TC", dwt: 63301, buildDate: "2016-09-01", yard: "Yangzhou Dayang Shipbuilding", engineType: "5S60ME-C8", mcr: 8300, eplMcrLimit: null, scrubber: false, esd: "No", paintVendor: "International" }),
  createMockVessel({ vesselName: "AESCHYLUS GRAECIA", design: "CS Marine 82BC", vesselType: "PANAMAX", imo: "9841964", status: "Redelivered", deliveryDate: "2025-06-13", minRedeliveryDate: "2025-10-26", maxRedeliveryDate: "2025-10-26", contractType: "Period TC", dwt: 82041, buildDate: "2019-11-01", yard: "Jiangsu Newyangzi Shipbuilding", engineType: "6S60ME", mcr: 9801, eplMcrLimit: null, scrubber: false, esd: "No", paintVendor: "Hempel" }),
  createMockVessel({ vesselName: "AFRICAN CONDOR", design: "Deltamarin 37", vesselType: "HANDY", imo: "9646900", status: "Redelivered", deliveryDate: "2025-02-12", minRedeliveryDate: "2025-03-19", maxRedeliveryDate: "2025-03-24", contractType: "Trip TC", dwt: 40481, buildDate: "2014-09-01", yard: "Tianjin Xingang Shipbuilding", engineType: "5S50ME", mcr: 6050, eplMcrLimit: 0, scrubber: false, esd: "No", paintVendor: "Jotun" }),
  createMockVessel({ vesselName: "AFROESSA", design: "Kurushima 78BC", vesselType: "PANAMAX", imo: "9666522", status: "Delivered", deliveryDate: "2026-06-22", minRedeliveryDate: "2026-09-15", maxRedeliveryDate: "2026-09-15", contractType: "Trip TC", dwt: 78175, buildDate: "2014-02-01", yard: "Shin Kurushima Toyohashi", engineType: "6S60MC", mcr: 9170, eplMcrLimit: 6660, scrubber: false, esd: "Yes", paintVendor: "International" }),
  createMockVessel({ vesselName: "AL ANOOD", design: null, vesselType: "PANAMAX", imo: "9837121", status: "Redelivered", deliveryDate: "2025-07-21", minRedeliveryDate: "2025-12-21", maxRedeliveryDate: "2026-03-08", contractType: "Period TC", dwt: 80681, buildDate: "2020-08-01", yard: "Hyundai Vietnam SB", engineType: "6S60ME", mcr: 14940, eplMcrLimit: null, scrubber: true, esd: "Yes", paintVendor: "Hempel", evaluatedVps: "2025-07-24" }),
  createMockVessel({ vesselName: "ALKIMOS", design: null, vesselType: "PANAMAX", imo: "9603001", status: "Redelivered", deliveryDate: "2024-11-06", minRedeliveryDate: "2024-12-16", maxRedeliveryDate: "2024-12-16", contractType: "Trip TC", dwt: 95308, buildDate: "2013-10-01", yard: "Jiangsu Eastern Heavy Industry", engineType: "6S60ME", mcr: 11650, eplMcrLimit: null, scrubber: true, esd: "No", paintVendor: "Jotun" }),
  createMockVessel({ vesselName: "AM TARANG", design: "SWS-180BC", vesselType: "CAPE", imo: "9832913", status: "Redelivered", deliveryDate: "2021-07-23", minRedeliveryDate: "2024-07-23", maxRedeliveryDate: "2025-02-07", contractType: "Period TC", dwt: 180885, buildDate: "2019-05-01", yard: "Shanghai Waigaoqiao Shbldg", engineType: "6G70ME", mcr: 15748, eplMcrLimit: 12771, scrubber: false, esd: "Yes", paintVendor: "International", currentAt125Knt: 34.6, cpAt125Knt: 35.2, cleanAt125Knt: 32.9, minMcrPercent: 30, maxMcrPercent: 90 }),
];

const MOCK_FILTER_KEYS: VesselEvaluationFilterKey[] = [
  "imo",
  "status",
  "vesselType",
  "buildYear",
  "paintVendor",
  "vesselName",
  "contractType",
  "design",
  "engineType",
  "scrubber",
];

// Applies the same filter, grouping, sort, and page contract locally without touching the API.
export const getMockVesselEvaluationDashboard = (
  request: VesselEvaluationDashboardRequest,
): VesselEvaluationDashboard => {
  const filteredVessels = filterVesselEvaluations(MOCK_VESSELS, request.filters);
  const groupedVessels = groupVesselEvaluationsByVesselName(filteredVessels);
  const sortedVessels = sortVesselEvaluations(groupedVessels, request.sort);
  const filterOptions = Object.fromEntries(
    MOCK_FILTER_KEYS.map((key) => [
      key,
      getVesselEvaluationFilterOptions(MOCK_VESSELS, request.filters, key),
    ]),
  ) as VesselEvaluationDashboard["filterOptions"];

  return {
    vessels: sortedVessels.slice(request.offset, request.offset + request.limit),
    totalVessels: groupedVessels.length,
    offset: request.offset,
    limit: request.limit,
    totalPages: Math.ceil(groupedVessels.length / request.limit),
    filterOptions,
    refreshedAt: "2026-07-25T07:04:54Z",
  };
};
